import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { dateCourte, getJournal, STATUTS } from '@/shared/api/bridge';
import {
  compterParStatut, EN_COURS, FILTRES_JOURNAL, filtrerJournal, paginer, refCourte,
} from '@/features/bridge/bridge-etat';
import ErreurBloc from '@/features/bridge/components/ErreurBloc';
import TiroirPreuve from '@/features/bridge/components/TiroirPreuve';

/** Le serveur plafonne à 500 lignes. On les prend toutes : filtrer et paginer 500
 *  lignes côté écran coûte moins qu'un aller-retour par page. */
const LIMITE = 500;
const PAR_PAGE = 20;
const LIBELLE_FILTRE = { tous: 'Tous', ...Object.fromEntries(
  Object.entries(STATUTS).map(([cle, s]) => [cle, s.label])) };

/**
 * Le journal, qui est aussi la preuve due à l'agrégateur.
 *
 * Il s'affichait en entier, cinquante lignes sans filtre, et la preuve d'un ordre
 * s'ouvrait tout en bas de la page, loin de la ligne cliquée. On filtre maintenant par
 * statut (un « Échec » se cherche, il ne se lit pas au milieu de quarante « Exécuté »),
 * on pagine, et la preuve s'ouvre en tiroir, à côté de la ligne.
 *
 * Les tentatives REFUSÉES restent dans le journal, et elles sont les plus parlantes :
 * c'est la trace d'un agrégateur qui a essayé d'atteindre ce qui n'est pas à lui.
 */
export default function OngletJournal({ agregateur }) {
  const [lignes, setLignes] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [filtre, setFiltre] = useState('tous');
  const [page, setPage] = useState(1);
  const [ordre, setOrdre] = useState(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      setLignes(await getJournal(agregateur, LIMITE));
    } catch (exc) {
      setErreur(exc);
    }
  }, [agregateur]);

  useEffect(() => { setLignes(null); setFiltre('tous'); setPage(1); charger(); }, [charger]);

  const comptes = useMemo(() => compterParStatut(lignes || []), [lignes]);
  const vue = useMemo(
    () => paginer(filtrerJournal(lignes || [], filtre), page, PAR_PAGE), [lignes, filtre, page]);

  if (erreur) return <ErreurBloc erreur={erreur} testid="bridge-journal-erreur" />;
  if (lignes === null) return <Skeleton className="h-48 w-full" />;
  if (lignes.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-neutral-300 px-4 py-8 text-center
                    text-[13px] text-neutral-500" data-testid="bridge-journal-vide">
        Aucun ordre reçu de cet agrégateur.
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="onglet-journal">
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filtrer par statut">
        {FILTRES_JOURNAL.map((f) => (
          <button key={f} type="button" aria-pressed={filtre === f}
                  onClick={() => { setFiltre(f); setPage(1); }}
                  disabled={comptes[f] === 0 && f !== 'tous'}
                  data-testid={`bridge-journal-filtre-${f}`}
                  className={`rounded-full border px-2.5 py-1 text-[12px] tabular-nums transition-colors
                    disabled:opacity-40 ${filtre === f
                      ? 'border-neutral-800 bg-neutral-800 text-white'
                      : 'border-neutral-300 text-neutral-600 hover:bg-neutral-50'}`}>
            {LIBELLE_FILTRE[f]} {comptes[f]}
          </button>
        ))}
        {lignes.length === LIMITE && (
          <span className="ml-auto text-[12px] text-amber-700">
            {LIMITE} derniers ordres : les plus anciens ne sont pas affichés.
          </span>
        )}
      </div>

      <div className="overflow-x-auto rounded-md border border-neutral-200">
        <table className="w-full min-w-[640px] text-[13px]">
          <thead className="bg-neutral-50 text-left text-[12px] text-neutral-500">
            <tr>
              <th className="px-3 py-2 font-normal">Reçu le</th>
              <th className="font-normal">Référence</th>
              <th className="font-normal">Ordre</th>
              <th className="font-normal">Statut</th>
              <th className="font-normal">Détail</th>
              <th className="w-24" />
            </tr>
          </thead>
          <tbody>
            {vue.items.map((l) => {
              const statut = STATUTS[l.statut || EN_COURS];
              return (
                <tr key={l.id || l.ordre_id} className="border-t border-neutral-100 align-top">
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">{dateCourte(l.recu_le)}</td>
                  <td><code className="text-[12px]" title={l.ref}>{refCourte(l.ref)}</code></td>
                  <td>{l.ordre}</td>
                  <td><Badge className={statut?.teinte}>{statut?.label || l.statut}</Badge></td>
                  <td className="max-w-[320px] text-neutral-600">
                    {l.erreur || l.execution?.detail || '—'}
                    {l.execution?.note && (
                      <span className="block text-[12px] text-neutral-400">{l.execution.note}</span>
                    )}
                  </td>
                  <td className="pr-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setOrdre(l)}
                            data-testid={`bridge-preuve-${l.ordre_id}`}>
                      Preuve
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {vue.pages > 1 && (
        <nav className="flex items-center justify-end gap-2 text-[12.5px] text-neutral-600"
             aria-label="Pages du journal">
          <Button size="sm" variant="outline" disabled={vue.page === 1}
                  onClick={() => setPage(vue.page - 1)}>Précédente</Button>
          <span className="tabular-nums">page {vue.page} sur {vue.pages} · {vue.total} ordres</span>
          <Button size="sm" variant="outline" disabled={vue.page === vue.pages}
                  onClick={() => setPage(vue.page + 1)}>Suivante</Button>
        </nav>
      )}

      <TiroirPreuve agregateur={agregateur} ligne={ordre} surFermeture={() => setOrdre(null)} />
    </div>
  );
}
